export const PARTICIPANT_TILE_KIND = "participant";
export const SCREEN_SHARE_TILE_KIND = "screen-share";
export const OVERFLOW_TILE_KIND = "overflow";
export const SCREEN_SHARE_TILE_ID = "screen-share";
export const ROOM_ADMIN_ROLE_LABEL = "Room admin";
export const CREATOR_ROLE_LABEL = "Creator";

export function getParticipantLayoutInitials(displayName) {
  return displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(token => token[0]?.toUpperCase())
    .join("");
}

export function getParticipantLayoutRoleLabel(roles = {}) {
  if (roles.owner) {
    return ROOM_ADMIN_ROLE_LABEL;
  }

  if (roles.creator) {
    return CREATOR_ROLE_LABEL;
  }

  return null;
}

export function getParticipantLayoutScreenShareOwner(participants, ownerId) {
  if (!participants.length) {
    return null;
  }

  return participants.find(participant => participant.id === ownerId) || participants[0];
}

export function createParticipantLayoutScreenShareId({ ownerId = "unknown", shareId = "default" } = {}) {
  return `${SCREEN_SHARE_TILE_ID}:${ownerId || "unknown"}:${shareId || "default"}`;
}

export function isParticipantLayoutScreenShareId(tileId) {
  return (
    typeof tileId === "string" && (tileId === SCREEN_SHARE_TILE_ID || tileId.startsWith(`${SCREEN_SHARE_TILE_ID}:`))
  );
}

export function getParticipantLayoutScreenShareOwnerId(tileId) {
  if (!isParticipantLayoutScreenShareId(tileId) || tileId === SCREEN_SHARE_TILE_ID) {
    return null;
  }

  const [, ownerId] = tileId.split(":");

  return ownerId || null;
}

export function createParticipantLayoutScreenShareParticipant({
  participants,
  ownerId,
  screenShareId = SCREEN_SHARE_TILE_ID,
  pinnedTileId = null,
  roomSpotlightTileId = null,
  canManageRoomSpotlight = false,
  roomSpotlightActive = false,
  mediaStream = null
} = {}) {
  const owner = getParticipantLayoutScreenShareOwner(participants || [], ownerId);
  const ownerDisplayName = owner?.displayName || "Shared";
  const spotlightState =
    canManageRoomSpotlight || roomSpotlightTileId || roomSpotlightActive
      ? {
          isRoomSpotlighted: roomSpotlightTileId === screenShareId,
          canToggleRoomSpotlight: Boolean(canManageRoomSpotlight),
          roomSpotlightActive: Boolean(roomSpotlightActive)
        }
      : {};

  return {
    id: screenShareId,
    kind: SCREEN_SHARE_TILE_KIND,
    displayName: `${ownerDisplayName}'s screen`,
    initials: "",
    isMuted: false,
    isPinned: pinnedTileId === screenShareId,
    ...spotlightState,
    isSelf: Boolean(owner?.isSelf),
    ownerId: owner?.id || null,
    ...(mediaStream ? { mediaStream } : {})
  };
}

export function canToggleParticipantLayoutMutedState({ canMuteUsers = false, participant } = {}) {
  if (!canMuteUsers || !participant) {
    return false;
  }

  if (participant.kind === SCREEN_SHARE_TILE_KIND) {
    return false;
  }

  if (participant.isSelf || participant.isMuted) {
    return false;
  }

  return true;
}

function getParticipantLayoutPriority(participant) {
  if (participant.isPinned) {
    return 0;
  }

  if (participant.kind === SCREEN_SHARE_TILE_KIND) {
    return 1;
  }

  if (participant.kind === PARTICIPANT_TILE_KIND && participant.mediaStream) {
    return 2;
  }

  return 3;
}

export function sortParticipantLayoutParticipants(participants) {
  return participants
    .map((participant, index) => ({ participant, index }))
    .sort((left, right) => {
      const priorityDifference =
        getParticipantLayoutPriority(left.participant) - getParticipantLayoutPriority(right.participant);

      if (priorityDifference !== 0) {
        return priorityDifference;
      }

      return left.index - right.index;
    })
    .map(({ participant }) => participant);
}

export function getEffectiveParticipantLayoutMode(mode, participants) {
  if (participants.some(participant => participant.isPinned)) {
    return "spotlight";
  }

  return mode;
}

function getRenderableTwoParticipantSpotlightParticipants(participants) {
  if (participants.length !== 2 || participants.some(participant => participant.isPinned)) {
    return participants.slice(0, 2);
  }

  const selfParticipant = participants.find(participant => participant.isSelf);
  const remoteParticipant = participants.find(participant => !participant.isSelf);

  if (selfParticipant && remoteParticipant) {
    return [remoteParticipant, selfParticipant];
  }

  return participants.slice(0, 2);
}

function getRenderableTwoParticipantScreenShareParticipants(participants) {
  if (participants.length !== 2) {
    return participants.slice(0, 2);
  }

  const screenShareParticipant = participants.find(participant => participant.kind === SCREEN_SHARE_TILE_KIND);
  const otherParticipant = participants.find(participant => participant.kind !== SCREEN_SHARE_TILE_KIND);

  if (screenShareParticipant && otherParticipant) {
    return [screenShareParticipant, otherParticipant];
  }

  return participants.slice(0, 2);
}

export function getRenderableParticipantLayoutParticipants(layout, participants) {
  if (layout.mode === "spotlight" && layout.variant === "stage-with-strip" && participants.length === 2) {
    return getRenderableTwoParticipantScreenShareParticipants(participants);
  }

  if (layout.mode === "spotlight" && layout.variant === "stage-with-pip") {
    return getRenderableTwoParticipantSpotlightParticipants(participants);
  }

  return participants.slice(0, layout.slotCount);
}
